import React from "react";
import "./styles.css";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

const baseUrl = "https://api.appetito.ro/";

// Daily prize wheel shown on the website ("Roata"): one spin per customer
// per day; a prize is good for their next order that same day.
const ITEM_TYPES = [
  {id: "try_again", label: "Mai încearcă"},
  {id: "free_delivery", label: "Livrare gratuită"},
  {id: "voucher", label: "Voucher (x lei)"},
  {id: "physical", label: "Premiu fizic"},
];

const typeLabel = (type) => (ITEM_TYPES.find((t) => t.id === type) || {}).label || type;

// Every text on the wheel screens. Empty = the website's default (shown as
// the placeholder), already translated in English.
const TEXTS = [
  {key: "title", label: "Titlu roată", ro: "Încearcă-ți norocul", en: "Try your luck"},
  {key: "subtitle", label: "Subtitlu roată", ro: "Învârte roata și descoperă ce îți rezervă norocul.", en: "Spin the wheel and find out what luck has in store for you."},
  {key: "description", label: "Descriere roată ({{count}} = nr. de elemente)", ro: "{{count}} rezultate posibile. O singură rotire pe zi.\nOrice premiu câștigi îți va fi livrat azi cu comanda!", en: "{{count}} possible outcomes. One spin per day.\nAny prize you win will be delivered today with your order!", multiline: true},
  {key: "spin_button", label: "Buton rotire", ro: "Învârte", en: "Spin"},
  {key: "win_title", label: "Titlu câștig", ro: "Felicitări!", en: "Congratulations!"},
  {key: "win_description", label: "Text câștig", ro: "Bucură-te de premiul tău!", en: "Enjoy your prize!"},
  {key: "free_delivery_description", label: "Text câștig livrare gratuită", ro: "Bucură-te de livrare gratuită!", en: "Enjoy free delivery!"},
  {key: "continue_button", label: "Buton câștig", ro: "Continuă", en: "Continue"},
  {key: "lose_title", label: "Titlu necâștigător", ro: "Mai încearcă mâine", en: "Try again tomorrow"},
  {key: "lose_subtitle", label: "Subtitlu necâștigător", ro: "Doar o rotire pe zi", en: "Only one spin per day"},
  {key: "lose_description", label: "Text necâștigător", ro: "Nu ai câștigat de data aceasta. Ai o singură rotire pe zi. Revino mâine pentru o nouă șansă.", en: "You didn't win this time. You get one spin per day. Come back tomorrow for a new chance.", multiline: true},
  {key: "close_button", label: "Buton necâștigător", ro: "Închide", en: "Close"},
];

// Odds are stored as weights in hundredths of a percent (2000 = 20%).
const toPercent = (weight) => String(Math.round(weight) / 100);
const toWeight = (percent) => Math.max(0, Math.round((parseFloat(String(percent).replace(",", ".")) || 0) * 100));
const formatPercent = (value) => `${Number(value.toFixed(2))}%`;

function emptyItem(position) {
  return {
    id: null,
    type: "free_delivery",
    label_ro: "",
    label_en: "",
    prize_text_ro: "",
    prize_text_en: "",
    value: "",
    image: "",
    stock: "",
    weight: "10", // percent
    position: String(position),
    active: true,
  };
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default class Roata extends React.Component {
  state = {
    loading: true,
    config: {enabled: false, partner_name: "", partner_logo: "", texts: {}},
    items: [],
    stats: [],
    spinsToday: 0,
    configSaving: false,
    configDirty: false,
    logoUploading: false,
    dialogOpen: false,
    form: emptyItem(1),
    formSaving: false,
    imageUploading: false,
    odds: {},
    oddsSaving: false,
  };

  componentDidMount() {
    this.load();
  }

  authHeaders = () => ({Authorization: `Bearer ${localStorage.getItem("access_token")}`});

  load = async () => {
    try {
      const response = await axios.get(`${baseUrl}v1/admin/wheel`, {headers: this.authHeaders()});
      const data = response.data || {};
      this.setState({
        config: {enabled: false, partner_name: "", partner_logo: "", ...data.config, texts: data.config?.texts || {}},
        items: data.items || [],
        odds: Object.fromEntries((data.items || []).map((item) => [item.id, toPercent(item.weight)])),
        stats: data.stats || [],
        spinsToday: data.spins_today || 0,
        configDirty: false,
      });
    } catch (e) {
      alert("Eroare la încărcarea roții.");
    } finally {
      this.setState({loading: false});
    }
  };

  uploadImage = async (file) => {
    const image = await readBase64(file);
    const extension = (file.name.split(".").pop() || "png").toLowerCase();
    const response = await axios.post(`${baseUrl}v1/admin/wheel/image`, {image, extension}, {headers: this.authHeaders()});
    return response.data.url;
  };

  // ---- General settings + texts

  updateConfig = (changes) => {
    this.setState({config: {...this.state.config, ...changes}, configDirty: true});
  };

  updateText = (key, lang) => (event) => {
    const {texts} = this.state.config;
    this.updateConfig({texts: {...texts, [key]: {...(texts[key] || {}), [lang]: event.target.value}}});
  };

  saveConfig = async (config = this.state.config) => {
    this.setState({configSaving: true});
    try {
      const response = await axios.put(`${baseUrl}v1/admin/wheel/config`, config, {headers: this.authHeaders()});
      this.setState({config: {...response.data, texts: response.data.texts || {}}, configDirty: false});
    } catch (e) {
      alert("Eroare la salvarea setărilor.");
    } finally {
      this.setState({configSaving: false});
    }
  };

  toggleEnabled = () => {
    const config = {...this.state.config, enabled: !this.state.config.enabled};
    this.setState({config});
    this.saveConfig(config);
  };

  onLogoSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    this.setState({logoUploading: true});
    try {
      this.updateConfig({partner_logo: await this.uploadImage(file)});
    } catch (e) {
      alert("Eroare la încărcarea logo-ului.");
    } finally {
      this.setState({logoUploading: false});
    }
  };

  // ---- Items

  openNewItem = () => {
    const nextPosition = this.state.items.reduce((max, item) => Math.max(max, item.position), 0) + 1;
    this.setState({dialogOpen: true, form: emptyItem(nextPosition)});
  };

  openEditItem = (item) => {
    this.setState({
      dialogOpen: true,
      form: {
        ...item,
        value: item.value ? String(item.value) : "",
        stock: String(item.stock || 0),
        weight: toPercent(item.weight),
        position: String(item.position),
      },
    });
  };

  closeDialog = () => this.setState({dialogOpen: false});

  updateField = (field) => (event) => {
    this.setState({form: {...this.state.form, [field]: event.target.value}});
  };

  onItemImageSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    this.setState({imageUploading: true});
    try {
      const url = await this.uploadImage(file);
      this.setState({form: {...this.state.form, image: url}});
    } catch (e) {
      alert("Eroare la încărcarea imaginii.");
    } finally {
      this.setState({imageUploading: false});
    }
  };

  saveItem = async () => {
    const {form} = this.state;
    if (!form.label_ro.trim()) {
      alert("Completează textul de pe roată (RO).");
      return;
    }
    const value = parseFloat(String(form.value).replace(",", "."));
    if (form.type === "voucher" && !(value > 0 && value <= 500)) {
      alert("Valoarea voucherului trebuie să fie între 0 și 500 lei.");
      return;
    }
    const payload = {
      type: form.type,
      label_ro: form.label_ro,
      label_en: form.label_en,
      prize_text_ro: form.prize_text_ro,
      prize_text_en: form.prize_text_en,
      value: form.type === "voucher" ? value : 0,
      image: form.image,
      stock: form.type === "physical" ? parseInt(form.stock, 10) || 0 : 0,
      weight: toWeight(form.weight),
      position: parseInt(form.position, 10) || 0,
      active: form.active,
    };

    this.setState({formSaving: true});
    try {
      if (form.id) {
        await axios.put(`${baseUrl}v1/admin/wheel/items/${form.id}`, payload, {headers: this.authHeaders()});
      } else {
        await axios.post(`${baseUrl}v1/admin/wheel/items`, payload, {headers: this.authHeaders()});
      }
      this.closeDialog();
      await this.load();
    } catch (e) {
      alert(e?.response?.data?.message || "Eroare la salvarea elementului.");
    } finally {
      this.setState({formSaving: false});
    }
  };

  updateOdds = (item) => (event) => {
    this.setState({odds: {...this.state.odds, [item.id]: event.target.value}});
  };

  saveOdds = async () => {
    const items = this.state.items.map((item) => ({id: item.id, weight: toWeight(this.state.odds[item.id])}));
    this.setState({oddsSaving: true});
    try {
      await axios.put(`${baseUrl}v1/admin/wheel/odds`, {items}, {headers: this.authHeaders()});
      await this.load();
    } catch (e) {
      alert(e?.response?.data?.message || "Eroare la salvarea șanselor.");
    } finally {
      this.setState({oddsSaving: false});
    }
  };

  toggleItemActive = async (item) => {
    try {
      await axios.put(`${baseUrl}v1/admin/wheel/items/${item.id}`, {...item, active: !item.active}, {headers: this.authHeaders()});
      await this.load();
    } catch (e) {
      alert("Eroare la salvarea elementului.");
    }
  };

  deleteItem = async (item) => {
    if (!window.confirm(`Ștergi „${item.label_ro}" de pe roată?`)) return;
    try {
      await axios.delete(`${baseUrl}v1/admin/wheel/items/${item.id}`, {headers: this.authHeaders()});
      await this.load();
    } catch (e) {
      alert("Eroare la ștergerea elementului.");
    }
  };

  // Mirrors the backend: switched-off items, 0 weight and physical prizes
  // out of stock never come up.
  isOnWheel = (item) => item.active && item.weight > 0 && (item.type !== "physical" || item.stock > 0);

  itemStats = (item) => this.state.stats.find((s) => s.item_id === item.id) || {won: 0, won_today: 0, used: 0};

  render() {
    const {
      loading, config, items, spinsToday, configSaving, configDirty, logoUploading,
      dialogOpen, form, formSaving, imageUploading,
    } = this.state;

    if (loading) {
      return (
        <div className="settings-container">
          <div className="loader-container"><div className="loader"/></div>
        </div>
      );
    }

    // Uses the odds being edited, so the real chance updates while typing.
    const {odds, oddsSaving} = this.state;
    const editedWeight = (item) => toWeight(odds[item.id] ?? toPercent(item.weight));
    const onWheel = (item) => this.isOnWheel({...item, weight: editedWeight(item)});
    const totalWeight = items.filter(onWheel).reduce((sum, item) => sum + editedWeight(item), 0);
    const chance = (item) => (onWheel(item) && totalWeight > 0 ? formatPercent((editedWeight(item) / totalWeight) * 100) : "—");
    const configuredTotal = items.filter((item) => item.active).reduce((sum, item) => sum + editedWeight(item), 0) / 100;
    const oddsDirty = items.some((item) => editedWeight(item) !== item.weight);
    const visibleCount = items.filter(this.isOnWheel).length;

    return (
      <div className="settings-container">
        <div className="settings-header-row">
          <h2 className="settings-title" style={{marginBottom: 0}}>Roata de premii</h2>
          <span className="settings-description">Rotiri azi: <strong>{spinsToday}</strong></span>
        </div>

        <div className="settings-section settings-section-wide">
          <div className="settings-section-header">
            <h3 className="settings-section-title">General</h3>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!configDirty || configSaving}
              onClick={() => this.saveConfig()}
            >
              Salvează
            </Button>
          </div>
          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-label">Roată activă</span>
              <span className="settings-description">
                Apare zilnic clienților logați pe site (o singură rotire pe zi). Premiul se aplică doar următoarei
                comenzi din aceeași zi: livrarea gratuită și premiul fizic la comenzi cu livrare, voucherul doar la plata cu cardul.
                {config.enabled && visibleCount === 0 && (
                  <strong style={{color: "#c62828"}}> Nu există niciun element activ — roata nu va apărea.</strong>
                )}
              </span>
            </div>
            <Switch checked={Boolean(config.enabled)} onChange={this.toggleEnabled} disabled={configSaving} color="primary"/>
          </div>

          <div className="settings-row" style={{alignItems: "flex-start"}}>
            <div className="settings-row-info">
              <span className="settings-label">Partener</span>
              <span className="settings-description">
                Logo-ul apare lângă cel Appetito („Pepsi × Appetito"). Fără logo, apare doar Appetito.
              </span>
              <TextField
                label="Nume partener"
                size="small"
                margin="dense"
                value={config.partner_name || ""}
                onChange={(e) => this.updateConfig({partner_name: e.target.value})}
                style={{maxWidth: 320}}
              />
            </div>
            <div className="wheel-logo-field">
              {config.partner_logo ? (
                <img src={config.partner_logo} alt="logo partener" className="wheel-logo-preview"/>
              ) : (
                <div className="wheel-logo-preview wheel-logo-empty">Fără logo</div>
              )}
              <div style={{display: "flex", gap: 8}}>
                <label className="banner-upload-button">
                  <input type="file" accept="image/*" onChange={this.onLogoSelected} disabled={logoUploading}/>
                  {logoUploading ? "Se încarcă..." : config.partner_logo ? "Schimbă" : "Încarcă logo"}
                </label>
                {config.partner_logo && (
                  <Button size="small" onClick={() => this.updateConfig({partner_logo: ""})}>Elimină</Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="settings-section settings-section-wide">
          <div className="settings-section-header">
            <h3 className="settings-section-title">Elemente roată</h3>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon/>} onClick={this.openNewItem}>
              Adaugă element
            </Button>
          </div>
          <div className="wheel-odds-bar">
            <span className="settings-description">
              Total șanse (elemente active):{" "}
              <strong style={{color: Math.abs(configuredTotal - 100) < 0.005 ? "#2e7d32" : "#c62828"}}>
                {formatPercent(configuredTotal)}
              </strong>
              {Math.abs(configuredTotal - 100) >= 0.005 && " — ar trebui să fie 100%; altfel se recalculează proporțional."}
              {" "}„Șansă reală" ține cont și de premiile fără stoc (ies de pe roată, iar restul își împart procentul lor).
            </span>
            <Button variant="contained" color="primary" size="small" disabled={!oddsDirty || oddsSaving} onClick={this.saveOdds}>
              Salvează șansele
            </Button>
          </div>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Text pe roată</TableCell>
                <TableCell>Valoare / stoc</TableCell>
                <TableCell>Șansă (%)</TableCell>
                <TableCell>Șansă reală</TableCell>
                <TableCell>Câștigate (azi) / folosite</TableCell>
                <TableCell>Activ</TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="settings-description">Niciun element. Adaugă cel puțin două.</TableCell>
                </TableRow>
              )}
              {items.map((item) => {
                const stats = this.itemStats(item);
                return (
                  <TableRow key={item.id} className={this.isOnWheel(item) ? "" : "settings-table-row-deleted"}>
                    <TableCell>{item.position}</TableCell>
                    <TableCell>{typeLabel(item.type)}</TableCell>
                    <TableCell>
                      <div className="wheel-item-label">
                        {item.image && <img src={item.image} alt="" className="wheel-item-thumb"/>}
                        <div>
                          <div>{item.label_ro}</div>
                          <div className="settings-description">{item.label_en || "—"}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.type === "voucher" && `${item.value} lei`}
                      {item.type === "physical" && (
                        <span style={{color: item.stock > 0 ? undefined : "#c62828"}}>{item.stock} buc.</span>
                      )}
                      {item.type !== "voucher" && item.type !== "physical" && "—"}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        className="wheel-odds-input"
                        inputProps={{min: 0, max: 100, step: 0.5}}
                        value={odds[item.id] ?? toPercent(item.weight)}
                        onChange={this.updateOdds(item)}
                      />
                    </TableCell>
                    <TableCell>{chance(item)}</TableCell>
                    <TableCell>{stats.won} ({stats.won_today}) / {stats.used}</TableCell>
                    <TableCell>
                      <Switch size="small" checked={item.active} onChange={() => this.toggleItemActive(item)} color="primary"/>
                    </TableCell>
                    <TableCell style={{whiteSpace: "nowrap"}}>
                      <IconButton size="small" title="Editează" onClick={() => this.openEditItem(item)}>
                        <EditIcon fontSize="small"/>
                      </IconButton>
                      <IconButton size="small" title="Șterge" onClick={() => this.deleteItem(item)}>
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="settings-section settings-section-wide">
          <div className="settings-section-header">
            <h3 className="settings-section-title">Texte</h3>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!configDirty || configSaving}
              onClick={() => this.saveConfig()}
            >
              Salvează
            </Button>
          </div>
          <p className="settings-description" style={{marginTop: 8}}>
            Lasă gol pentru textul implicit (afișat în câmp). Textul în engleză apare clienților cu site-ul în engleză.
          </p>
          {TEXTS.map((text) => (
            <div className="wheel-text-row" key={text.key}>
              <span className="settings-label">{text.label}</span>
              <div className="wheel-text-fields">
                {["ro", "en"].map((lang) => (
                  <TextField
                    key={lang}
                    label={lang.toUpperCase()}
                    size="small"
                    variant="outlined"
                    fullWidth
                    multiline={Boolean(text.multiline)}
                    placeholder={text[lang]}
                    InputLabelProps={{shrink: true}}
                    value={config.texts[text.key]?.[lang] || ""}
                    onChange={this.updateText(text.key, lang)}
                  />
                ))}
              </div>
            </div>
          ))}
          {configDirty && (
            <p className="settings-description" style={{color: "#e97736"}}>Ai modificări nesalvate (partener / texte).</p>
          )}
        </div>

        <Dialog open={dialogOpen} onClose={this.closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{form.id ? "Editează element" : "Adaugă element"}</DialogTitle>
          <DialogContent>
            <TextField select label="Tip" fullWidth margin="dense" value={form.type} onChange={this.updateField("type")}>
              {ITEM_TYPES.map((type) => (
                <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
              ))}
            </TextField>
            {form.type === "voucher" && (
              <TextField label="Valoare (lei)" type="number" fullWidth margin="dense" value={form.value} onChange={this.updateField("value")}/>
            )}
            {form.type === "physical" && (
              <TextField
                label="Stoc (bucăți)"
                type="number"
                fullWidth
                margin="dense"
                value={form.stock}
                onChange={this.updateField("stock")}
                helperText="Scade cu 1 la fiecare câștig. La 0 premiul dispare de pe roată."
              />
            )}
            <div className="wheel-text-fields">
              <TextField label="Text pe roată (RO)" fullWidth margin="dense" placeholder="ex: doza Pepsi 0.33ml" value={form.label_ro} onChange={this.updateField("label_ro")}/>
              <TextField label="Text pe roată (EN)" fullWidth margin="dense" placeholder="ex: Pepsi can 0.33l" value={form.label_en} onChange={this.updateField("label_en")}/>
            </div>
            {form.type !== "try_again" && (
              <div className="wheel-text-fields">
                <TextField
                  label="Mesaj câștig (RO)"
                  fullWidth
                  margin="dense"
                  placeholder={form.type === "physical" ? "ex: Ai câștigat o doză de Pepsi" : "implicit"}
                  helperText="Subtitlul de sub „Felicitări!”"
                  value={form.prize_text_ro}
                  onChange={this.updateField("prize_text_ro")}
                />
                <TextField
                  label="Mesaj câștig (EN)"
                  fullWidth
                  margin="dense"
                  placeholder={form.type === "physical" ? "ex: You won a can of Pepsi" : "implicit"}
                  value={form.prize_text_en}
                  onChange={this.updateField("prize_text_en")}
                />
              </div>
            )}
            {form.type === "physical" && (
              <div style={{display: "flex", alignItems: "center", gap: 12, marginTop: 12}}>
                {form.image && <img src={form.image} alt="" className="wheel-item-thumb wheel-item-thumb-large"/>}
                <label className="banner-upload-button">
                  <input type="file" accept="image/*" onChange={this.onItemImageSelected} disabled={imageUploading}/>
                  {imageUploading ? "Se încarcă..." : form.image ? "Schimbă imaginea" : "Alege imagine (PNG transparent)"}
                </label>
                {form.image && <Button size="small" onClick={() => this.setState({form: {...form, image: ""}})}>Elimină</Button>}
              </div>
            )}
            <div className="wheel-text-fields">
              <TextField
                label="Șansă (%)"
                type="number"
                fullWidth
                margin="dense"
                inputProps={{min: 0, max: 100, step: 0.5}}
                value={form.weight}
                onChange={this.updateField("weight")}
                helperText="0 = nu iese niciodată"
              />
              <TextField
                label="Poziție pe roată"
                type="number"
                fullWidth
                margin="dense"
                value={form.position}
                onChange={this.updateField("position")}
              />
            </div>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => this.setState({form: {...form, active: e.target.checked}})} color="primary"/>}
              label="Activ"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} disabled={formSaving}>Anulează</Button>
            <Button onClick={this.saveItem} color="primary" variant="contained" disabled={formSaving || imageUploading}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
